import { useState } from "react";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";

import {
    Alert,
    alpha,
    Box,
    Button,

    Dialog,

    DialogActions,

    DialogContent,

    DialogTitle,

    List,

    ListItem,

    ListItemIcon,

    ListItemText,

    Stack,

    Typography,

} from "@mui/material";

import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";

import axios from "axios";

import { buildApiUrl } from "../../config/api";

import {
    PLAN_INCLUDES,
    TRIAL_EXTENSION_DAYS,
    getTrialPeriodLabel,
    subscriptionBlockedMessage,
} from "../../config/subscriptionPlans";
import { requestTrialExtension } from "../../utils/subscriptionTrialExtensionApi";

import { ENABLE_SUBSCRIPTION_ONLINE_PAYMENT } from "../../config/featureFlags";

import SubscriptionPaymentDialog from "./SubscriptionPaymentDialog";

import Popup from "../Popup";



/**

 * Blocks the school office when trial or paid period has ended.

 * Closes automatically when subscriptionActive becomes true (after payment confirmed).

 */

export default function SubscriptionExpiredModal({ open, user, onSubscriptionUpdated }) {

    const [paymentOpen, setPaymentOpen] = useState(false);
    const [requestingExtension, setRequestingExtension] = useState(false);
    const [popup, setPopup] = useState({ show: false, message: "", severity: "success" });

    if (!open || !user) return null;

    const extensionStatus = user.trialExtensionRequestStatus;
    const canRequestExtension = Boolean(user.canRequestTrialExtension);
    const extensionDays = user.trialExtensionDays || TRIAL_EXTENSION_DAYS;
    const trialExpired =
        user.subscriptionReason === "trial_expired" ||
        (user.hasUsedTrial && user.subscriptionReason !== "subscription_expired");



    const planName = user.subscriptionPlanName || user.subscriptionPlan || "your plan";

    const priceLabel = user.planPriceLabel || "";

    const billing = user.planBillingLabel || "per month";

    const studentLimit =

        user.studentLimit != null

            ? `Up to ${user.studentLimit} students on this plan`

            : "Unlimited students on this plan";

    const schoolId = user._id || user.id;



    const handleRequestExtension = async () => {
        setRequestingExtension(true);
        try {
            const data = await requestTrialExtension({ schoolId });
            if (onSubscriptionUpdated) onSubscriptionUpdated(data);
            setPopup({
                show: true,
                message: data.message || "Trial extension request submitted.",
                severity: "success",
            });
        } catch (err) {
            setPopup({
                show: true,
                message: err.response?.data?.message || err.message || "Could not submit request.",
                severity: "error",
            });
        } finally {
            setRequestingExtension(false);
        }
    };

    const refreshStatus = async () => {

        try {

            const res = await axios.get(buildApiUrl(`/Subscription/${schoolId}`));

            if (res.data?.subscriptionActive && onSubscriptionUpdated) {

                onSubscriptionUpdated(res.data);

            }

        } catch {

            /* ignore */

        }

    };



    return (

        <>

            <Dialog

                open={open && !paymentOpen}

                maxWidth="sm"

                fullWidth

                disableEscapeKeyDown

                aria-labelledby="subscription-expired-title"

            >

                <DialogTitle id="subscription-expired-title" sx={{ pb: 1 }}>

                    <Stack direction="row" spacing={1.5} alignItems="center">

                        <Box

                            sx={{

                                width: 44,

                                height: 44,

                                borderRadius: 2,

                                display: "flex",

                                alignItems: "center",

                                justifyContent: "center",

                                bgcolor: (t) => alpha(t.palette.warning.main, 0.12),

                                color: "warning.main",

                            }}

                        >

                            <LockOutlinedIcon />

                        </Box>

                        <Box>

                            <Typography variant="h6" fontWeight={700}>

                                Subscription payment required

                            </Typography>

                            <Typography variant="body2" color="text.secondary">

                                {subscriptionBlockedMessage(user)}

                            </Typography>

                        </Box>

                    </Stack>

                </DialogTitle>

                <DialogContent dividers>

                    <Typography variant="subtitle2" gutterBottom>

                        Your plan: {planName}

                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>

                        {studentLimit} · Amount due: <strong>{priceLabel}</strong> ({billing})

                    </Typography>



                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Pay via mobile money to restore access immediately. Your {getTrialPeriodLabel()} free trial applies only
                        once per school at registration.
                    </Typography>

                    {trialExpired && extensionStatus === "pending" ? (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Your request for a {extensionDays}-day trial extension is pending review by the platform team.
                            You will regain access if it is approved.
                        </Alert>
                    ) : null}

                    {trialExpired && extensionStatus === "rejected" ? (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Your trial extension request was not approved. Each school may only request one extension.
                            Please subscribe to continue.
                        </Alert>
                    ) : null}

                    {trialExpired && user?.trialExtensionUsed ? (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Your school has already used its one trial extension. Please subscribe to continue.
                        </Alert>
                    ) : null}



                    <List dense disablePadding sx={{ mb: 2 }}>

                        {PLAN_INCLUDES.map((item) => (

                            <ListItem key={item} disableGutters sx={{ py: 0.25 }}>

                                <ListItemIcon sx={{ minWidth: 32 }}>

                                    <CheckCircleOutlineIcon color="primary" fontSize="small" />

                                </ListItemIcon>

                                <ListItemText primary={item} primaryTypographyProps={{ variant: "body2" }} />

                            </ListItem>

                        ))}

                    </List>

                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2, flexWrap: "wrap", gap: 1 }}>
                    <Button onClick={refreshStatus}>Check again</Button>
                    {trialExpired && canRequestExtension ? (
                        <Button
                            variant="outlined"
                            startIcon={<ScheduleOutlinedIcon />}
                            disabled={requestingExtension}
                            onClick={handleRequestExtension}
                        >
                            {requestingExtension ? "Submitting…" : `Request ${extensionDays}-day extension`}
                        </Button>
                    ) : null}
                    {ENABLE_SUBSCRIPTION_ONLINE_PAYMENT ? (

                        <Button

                            variant="contained"

                            startIcon={<PhoneAndroidIcon />}

                            onClick={() => setPaymentOpen(true)}

                        >

                            Pay via mobile money

                        </Button>

                    ) : null}

                </DialogActions>

            </Dialog>



            {ENABLE_SUBSCRIPTION_ONLINE_PAYMENT ? (

                <SubscriptionPaymentDialog

                    open={paymentOpen}

                    onClose={() => setPaymentOpen(false)}

                    adminId={schoolId}

                    planId={user.subscriptionPlan}

                    schoolName={user.schoolName}

                    adminEmail={user.email}

                    adminName={user.name}

                    onPaymentConfirmed={async () => {

                        setPaymentOpen(false);

                        await refreshStatus();

                        setPopup({

                            show: true,

                            message: "Payment received. Your subscription is now active.",

                            severity: "success",

                        });

                    }}

                />

            ) : null}



            <Popup

                message={popup.message}

                showPopup={popup.show}

                setShowPopup={(v) => setPopup((p) => ({ ...p, show: v }))}

                severity={popup.severity}

            />

        </>

    );

}

